import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { MODULE_ICON_CLASSES } from '@/shared/nav/moduleIcons';
import { listedModulesFor } from '@/shared/nav/modules';
import { useOrg } from '@/shared/org';
import { editorPathFor, folderPath, foldersApi, templateFor } from '@/shared/folders';
import type { FoldersNode } from '@/shared/folders';

interface Props {
  value: string;
  onValueChange: (value: string) => void;
  /** Enter, or the "see all" row: switch the page body to the results view. */
  onSubmit: (term: string) => void;
}

const DEBOUNCE_MS = 200;
const MAX_SUGGESTIONS = 8;

export interface ModuleMatch {
  readonly id: string;
  readonly path: string;
  readonly name: string;
  readonly icon: Parameters<typeof Icon>[0]['name'];
  readonly chipClass: string;
}

interface Suggestion {
  readonly key: string;
  readonly to: string;
  readonly label: string;
  readonly detail: string;
  readonly icon: Parameters<typeof Icon>[0]['name'];
  readonly chipClass: string;
}

/** Modules are matched here rather than on the server: the registry is already
 *  in memory and a round-trip to name twelve strings would be absurd. */
export function useModuleMatches(term: string): readonly ModuleMatch[] {
  const { t } = useI18n();
  const org = useOrg();
  const isPlatform = org.status === 'ready' && org.membership.organization.isPlatform;
  const needle = term.trim().toLocaleLowerCase();

  // `listedModulesFor` is called inside the memo rather than outside it: it builds a
  // fresh array on every call, so as a dependency it would defeat the memo.
  return useMemo(() => {
    if (needle.length < 2) return [];
    return listedModulesFor(isPlatform)
      .map((m) => ({
        id: m.id,
        path: m.path,
        name: t(m.nameKey),
        icon: m.icon,
        chipClass: MODULE_ICON_CLASSES[m.id],
      }))
      .filter((m) => m.name.toLocaleLowerCase().includes(needle));
  }, [isPlatform, needle, t]);
}

export function HomeSearch({ value, onValueChange, onSubmit }: Props) {
  const { t } = useI18n();
  const org = useOrg();
  const navigate = useNavigate();
  const orgId = org.status === 'ready' ? org.orgId : null;
  const listId = useId();

  const [nodes, setNodes] = useState<readonly FoldersNode[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapper = useRef<HTMLDivElement>(null);

  const moduleMatches = useModuleMatches(value);

  useEffect(() => {
    const term = value.trim();
    if (!orgId || term.length < 2) {
      setNodes([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void foldersApi
        .searchNodes(orgId, term, controller.signal)
        .then((found) => { if (!controller.signal.aborted) setNodes(found); })
        .catch(() => { if (!controller.signal.aborted) setNodes([]); });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [orgId, value]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', onPointer);
    return () => window.removeEventListener('pointerdown', onPointer);
  }, [open]);

  const suggestions: readonly Suggestion[] = useMemo(() => {
    const fromModules: Suggestion[] = moduleMatches.map((m) => ({
      key: `module:${m.id}`,
      to: `/${m.path}`,
      label: m.name,
      detail: t('home.apps'),
      icon: m.icon,
      chipClass: m.chipClass,
    }));
    const fromNodes: Suggestion[] = nodes.map((node) => {
      const template = templateFor(node.docType);
      return {
        key: `node:${node.id}`,
        to: node.kind === 'folder' ? folderPath(node.id) : editorPathFor(node.id, node.docType),
        label: node.name,
        detail: node.kind === 'folder' ? t('folders.folders') : t(template?.nameKey ?? 'folders.files'),
        icon: node.kind === 'folder' ? ('folder' as const) : (template?.icon ?? ('sheets' as const)),
        chipClass: 'bg-surface-3 text-muted',
      };
    });
    return [...fromModules, ...fromNodes].slice(0, MAX_SUGGESTIONS);
  }, [moduleMatches, nodes, t]);

  const canSuggest = value.trim().length >= 2;
  const showList = open && canSuggest;

  const choose = (index: number) => {
    const suggestion = suggestions[index];
    setOpen(false);
    if (suggestion) navigate(suggestion.to);
    else onSubmit(value.trim());
  };

  return (
    <div ref={wrapper} className="relative min-w-0 flex-1 sm:max-w-xl">
      <div className="flex h-11 items-center gap-2 rounded-full border border-border bg-surface-2 px-4 text-muted focus-within:border-accent">
        <Icon name="search" className="h-4 w-4 shrink-0" />
        <input
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={t('home.search.label')}
          placeholder={t('home.search.placeholder')}
          value={value}
          onChange={(event) => {
            onValueChange(event.target.value);
            setActive(-1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              if (!showList) return;
              event.preventDefault();
              const step = event.key === 'ArrowDown' ? 1 : -1;
              // -1 is "nothing highlighted", which is what Enter needs to mean
              // "show every result" rather than "open the first one".
              setActive((current) => {
                const next = current + step;
                if (next < -1) return suggestions.length - 1;
                if (next >= suggestions.length) return -1;
                return next;
              });
              return;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              if (!canSuggest) return;
              choose(active);
            }
          }}
          // The browser draws its own clear button on type="search"; ours is below.
          className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-muted [&::-webkit-search-cancel-button]:appearance-none"
        />
        {value && (
          <button
            type="button"
            aria-label={t('home.search.clear')}
            onClick={() => {
              onValueChange('');
              onSubmit('');
              setOpen(false);
            }}
            className="shrink-0 rounded-full p-1 text-muted hover:text-fg"
          >
            <Icon name="close" className="h-4 w-4" />
          </button>
        )}
      </div>

      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-2xl border border-border bg-surface shadow-lg"
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.key} role="option" aria-selected={index === active}>
              <button
                type="button"
                onClick={() => choose(index)}
                className={`tap-target flex w-full items-center gap-3 px-3 text-left ${index === active ? 'bg-surface-3' : 'hover:bg-surface-3'}`}
              >
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-token ${suggestion.chipClass}`}>
                  <Icon name={suggestion.icon} className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-fg">{suggestion.label}</span>
                <span className="shrink-0 text-xs text-muted">{suggestion.detail}</span>
              </button>
            </li>
          ))}
          <li role="option" aria-selected={active === -1}>
            <button
              type="button"
              onClick={() => choose(-1)}
              className={`tap-target flex w-full items-center gap-3 border-t border-border px-3 text-left text-sm text-muted ${active === -1 ? 'bg-surface-3' : 'hover:bg-surface-3'}`}
            >
              <Icon name="search" className="h-4 w-4 shrink-0" />
              <span className="min-w-0 truncate">{t('home.search.seeAll', { term: value.trim() })}</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
